import { MapPin, CheckCircle2, Sparkles } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';

export function Hero() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const premiumBrands = [
    'Porsche', 'BMW M', 'Mercedes-AMG', 'Audi RS', 'Bentley', 'Lamborghini', 'Tesla', 'Elektrické vozidlá', 'Hybridné vozidlá'
  ];

  const technologies = [
    { icon: '⚙️', label: 'Pneuservis Trenčín' },
    { icon: '⚡', label: '3D Geometria' },
    { icon: '📡', label: 'ADAS Kalibrácia' },
    { icon: '🔬', label: 'Diagnostika TOPDON' },
    { icon: '🔧', label: 'Autoservis všetky značky' },
    { icon: '🎨', label: 'Premium Detailing' },
  ];

  const stats = [
    { icon: Sparkles, label: 'ADAS', sublabel: 'Kalibrácia' },
    { icon: CheckCircle2, label: '10+', sublabel: 'Rokov praxe' },
    { icon: Sparkles, label: '€300K+', sublabel: 'Technológie' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-24 bg-black">
      {/* Modern gradient background */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-950 to-black" />
        <div className="absolute top-1/4 right-1/4 w-[800px] h-[800px] bg-lime-500/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 left-1/4 w-[600px] h-[600px] bg-lime-600/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `linear-gradient(to right, rgb(255 255 255 / 0.1) 1px, transparent 1px),
                          linear-gradient(to bottom, rgb(255 255 255 / 0.1) 1px, transparent 1px)`,
        backgroundSize: '80px 80px'
      }} />

      <div className={`relative z-10 container mx-auto px-6 transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}>
        
        <div className="max-w-6xl mx-auto text-center">
          
          {/* Modern location badge */}
          <Badge 
            variant="secondary"
            className="mb-8 px-6 py-3 bg-zinc-900/50 backdrop-blur-sm border border-lime-500/20 hover:border-lime-500/40 transition-all"
          >
            <MapPin className="w-4 h-4 mr-2 text-lime-400" />
            <span className="text-sm font-semibold text-lime-400">Trenčín</span>
            <span className="mx-2 text-zinc-600">•</span>
            <span className="text-sm text-zinc-400">Bratislavská 971/911 05</span>
          </Badge>

          {/* SEO-optimized H1 with modern styling */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6 tracking-tight leading-[1.1]">
            Autoservis Trenčín
            <span className="block mt-3 bg-gradient-to-r from-lime-400 via-lime-300 to-lime-500 bg-clip-text text-transparent">
              ADAS · Pneuservis · 3D Geometria
            </span>
          </h1>

          {/* Value proposition */}
          <p className="text-xl md:text-2xl text-zinc-300 max-w-3xl mx-auto mb-4 leading-relaxed">
            Profesionálny autoservis s{' '}
            <span className="text-lime-400 font-semibold">ADAS kalibráciou</span>,
            pneuservisom a 3D geometriou v Trenčíne.
          </p>
          
          <p className="text-lg text-zinc-500 mb-12">
            Špecializácia na prémiové značky: BMW, Porsche, Mercedes, Audi
          </p>

          {/* Premium brands with modern badges */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-12 max-w-4xl mx-auto">
            {premiumBrands.map((brand, i) => (
              <Badge 
                key={i}
                variant="outline"
                className={`px-3 py-1.5 bg-zinc-900/30 border-zinc-800 hover:border-lime-500/40 hover:bg-zinc-900/50 transition-all duration-300 cursor-default ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
                }`}
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <span className="text-sm text-zinc-400">{brand}</span>
              </Badge>
            ))}
          </div>

          {/* Technology cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-12">
            {technologies.map((tech, i) => (
              <Card 
                key={i}
                className={`p-4 bg-zinc-900/50 backdrop-blur-sm border-zinc-800 hover:border-lime-500/40 hover:bg-zinc-900/70 transition-all duration-300 cursor-default ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="text-2xl mb-2">{tech.icon}</div>
                <div className="text-xs text-zinc-400 font-medium leading-tight">{tech.label}</div>
              </Card>
            ))}
          </div>

          {/* CTA Buttons with modern styling */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <Button 
              asChild 
              size="lg"
              className="px-10 py-6 text-lg font-semibold bg-lime-400 hover:bg-lime-300 text-black rounded-xl transition-all hover:scale-105 shadow-lg shadow-lime-500/20"
            >
              <Link to="/rezervacia" className="flex items-center gap-2">
                <span>Konzultácia zadarmo</span>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => {
                document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="px-10 py-6 text-lg font-semibold border-2 border-zinc-700 hover:border-lime-500 bg-zinc-900/50 hover:bg-zinc-900 text-white rounded-xl transition-all cursor-pointer"
            >
              Naše služby
            </Button>
          </div>

          {/* Modern stats cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <Card 
                  key={i}
                  className="p-6 bg-zinc-900/50 backdrop-blur-sm border-zinc-800 hover:border-lime-500/40 transition-all"
                >
                  <Icon className="w-8 h-8 text-lime-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white mb-1">{stat.label}</div>
                  <div className="text-sm text-zinc-500 uppercase tracking-wider">{stat.sublabel}</div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
